import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ProfileUsers } from '../../../../../../api/enumerations';
import { fetchAllPages } from '../../../../../../api/fetchAllPages';
import { getInAccreditation } from '../../../../../../api/theHive/redeProto';
import { InAccreditationItem } from '../../../../../../api/theHive/redeProto/types';
import { cleanValue, normalizeText } from '../../../../../../helpers';
import { useCurrentRole } from '../../../../../../hooks/useCurrentRole';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';
import { AbandonedFilter, AtivoFilter } from './types';
import {
  agingBucketKey,
  agingInDays,
  InAccreditationSummary,
  summarize,
} from './utils';

/** Linhas por página da tabela. */
const PER_PAGE = 100;
/** Tamanho de página usado para carregar a base do edital. */
const FETCH_SIZE = 200;
/**
 * Teto de carga: 50 × 200 = 10.000 pedidos. Acima disso a tela assume que não
 * tem a base inteira e troca de modo (ver `truncated`), em vez de seguir
 * respondendo com número parcial como se fosse o total.
 */
const MAX_PAGES = 50;

export type InAccreditationOrder = 'fifo' | 'recent';

interface UseInAccreditationHook {
  role: number | null;
  isAllowed: boolean;
  items: InAccreditationItem[];
  summary: InAccreditationSummary;
  /**
   * `true` = a base excede o teto de carga. Indicadores cobrem só o que foi
   * carregado (os mais antigos), busca/ordenação/paginação vão para a API e os
   * filtros client-side são desativados.
   */
  truncated: boolean;
  filteredCount: number;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  loading: boolean;
  expanded: number[];
  toggleExpand: (id: number) => void;
  order: InAccreditationOrder;
  setOrder: (order: InAccreditationOrder) => void;
  searchTerm: string;
  searchDisabled: boolean;
  handleSearchChange: (value: string) => void;
  handleClearSearch: () => void;
  ativoFilter: AtivoFilter;
  setAtivoFilter: (value: AtivoFilter) => void;
  abandonedFilter: AbandonedFilter;
  setAbandonedFilter: (value: AbandonedFilter) => void;
  agingFilter: string;
  setAgingFilter: (value: string) => void;
  hasFilters: boolean;
  handleClearFilters: () => void;
}

export default function useInAccreditation(): UseInAccreditationHook {
  const role = useCurrentRole();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const [baseItems, setBaseItems] = useState<InAccreditationItem[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loadingBase, setLoadingBase] = useState(false);

  // Usados só no modo truncado, quando a tabela volta a ser server-side.
  const [serverItems, setServerItems] = useState<InAccreditationItem[]>([]);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [loadingServer, setLoadingServer] = useState(false);

  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [order, setOrderState] = useState<InAccreditationOrder>('fifo');
  const [searchTerm, setSearchTerm] = useState('');
  const [ativoFilter, setAtivoFilterState] = useState<AtivoFilter>('all');
  const [abandonedFilter, setAbandonedFilterState] =
    useState<AbandonedFilter>('all');
  const [agingFilter, setAgingFilterState] = useState('all');
  const debouncedSearch = useDebounce(searchTerm);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  // Descarta resposta de requisição superada: trocar de edital rápido fazia o
  // resultado antigo chegar depois e sobrescrever o do edital atual.
  const baseRunRef = useRef(0);
  const serverRunRef = useRef(0);

  const isAllowed =
    role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

  const toggleExpand = useCallback((id: number): void => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const fetchBase = useCallback(async (): Promise<void> => {
    const runId = baseRunRef.current + 1;
    baseRunRef.current = runId;

    setLoadingBase(true);
    try {
      const result = await fetchAllPages<InAccreditationItem>(
        (pageNumber) =>
          getInAccreditation(selectedNoticeId, pageNumber, FETCH_SIZE, 'fifo'),
        { maxPages: MAX_PAGES }
      );
      if (baseRunRef.current !== runId) return;
      setBaseItems(result.rows);
      setTruncated(result.truncated);
    } catch (error) {
      if (baseRunRef.current !== runId) return;
      setBaseItems([]);
      setTruncated(false);
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (baseRunRef.current === runId) setLoadingBase(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    if (isAllowed) fetchBase();
  }, [fetchBase, isAllowed]);

  // Trocar de edital reinicia a navegação da tabela.
  useEffect(() => {
    setPage(1);
    setExpanded([]);
  }, [selectedNoticeId]);

  /** Modo truncado: a tabela é servida pela API, com busca e ordem exatas. */
  const fetchServerPage = useCallback(async (): Promise<void> => {
    const runId = serverRunRef.current + 1;
    serverRunRef.current = runId;

    setLoadingServer(true);
    try {
      const response = await getInAccreditation(
        selectedNoticeId,
        page,
        PER_PAGE,
        order,
        debouncedSearch
      );
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      if (serverRunRef.current !== runId) return;
      setServerItems(response.data);
      setServerTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      if (serverRunRef.current !== runId) return;
      setServerItems([]);
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (serverRunRef.current === runId) setLoadingServer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId, page, order, debouncedSearch]);

  useEffect(() => {
    if (isAllowed && truncated) fetchServerPage();
  }, [isAllowed, truncated, fetchServerPage]);

  const summary = useMemo(() => summarize(baseItems), [baseItems]);

  const filtered = useMemo(() => {
    if (truncated) return [];

    const term = normalizeText(debouncedSearch.trim());
    const termDigits = cleanValue(debouncedSearch);

    const rows = baseItems.filter((item) => {
      if (ativoFilter !== 'all' && item.ativo !== ativoFilter) return false;
      if (abandonedFilter === 'only' && !item.abandoned) return false;
      if (abandonedFilter === 'hide' && item.abandoned) return false;
      if (
        agingFilter !== 'all' &&
        agingBucketKey(agingInDays(item.requestedAt)) !== agingFilter
      ) {
        return false;
      }
      if (!term) return true;
      return (
        normalizeText(item.companyName).includes(term) ||
        (termDigits.length > 0 && cleanValue(item.cnpj).includes(termDigits))
      );
    });

    // Ordena por data do pedido; sem data vai para o fim nas duas direções.
    return rows.sort((a, b) => {
      if (!a.requestedAt) return 1;
      if (!b.requestedAt) return -1;
      const diff = a.requestedAt.localeCompare(b.requestedAt);
      return order === 'fifo' ? diff : -diff;
    });
  }, [
    truncated,
    baseItems,
    debouncedSearch,
    ativoFilter,
    abandonedFilter,
    agingFilter,
    order,
  ]);

  const clientTotalPages = Math.max(Math.ceil(filtered.length / PER_PAGE), 1);
  const totalPages = truncated ? serverTotalPages : clientTotalPages;

  // Um filtro pode encurtar a lista abaixo da página atual.
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const clientItems = useMemo(
    () => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filtered, page]
  );

  const items = truncated ? serverItems : clientItems;
  const loading = loadingBase || (truncated && loadingServer);

  const handleSearchChange = useCallback((value: string): void => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handleClearSearch = useCallback((): void => {
    setSearchTerm('');
    setPage(1);
  }, []);

  const setOrder = useCallback((next: InAccreditationOrder): void => {
    setOrderState(next);
    setPage(1);
  }, []);

  const setAtivoFilter = useCallback((next: AtivoFilter): void => {
    setAtivoFilterState(next);
    setPage(1);
  }, []);

  const setAbandonedFilter = useCallback((next: AbandonedFilter): void => {
    setAbandonedFilterState(next);
    setPage(1);
  }, []);

  const setAgingFilter = useCallback((next: string): void => {
    setAgingFilterState(next);
    setPage(1);
  }, []);

  const hasFilters =
    ativoFilter !== 'all' || abandonedFilter !== 'all' || agingFilter !== 'all';

  const handleClearFilters = useCallback((): void => {
    setAtivoFilterState('all');
    setAbandonedFilterState('all');
    setAgingFilterState('all');
    setPage(1);
  }, []);

  // Nada a buscar com zero empresas; segue habilitado enquanto há busca ativa
  // para o filtro poder ser limpo.
  const searchDisabled =
    !searchTerm && !loading && baseItems.length === 0 && !truncated;

  return {
    role,
    isAllowed,
    items,
    summary,
    truncated,
    filteredCount: truncated ? summary.total : filtered.length,
    page,
    setPage,
    totalPages,
    loading,
    expanded,
    toggleExpand,
    order,
    setOrder,
    searchTerm,
    searchDisabled,
    handleSearchChange,
    handleClearSearch,
    ativoFilter,
    setAtivoFilter,
    abandonedFilter,
    setAbandonedFilter,
    agingFilter,
    setAgingFilter,
    hasFilters,
    handleClearFilters,
  };
}
